import { finalize, take } from 'rxjs/operators';import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { MasterDataService } from 'src/app/Services/master-data.service';
import { CustomerDTO } from 'src/app/DTOs/CustomerDTO';
import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';
import {
  InspectionTypeDetailDTO,
  InspectionTypeOverviewResponse
} from 'src/app/DTOs/InspectionMasterData/InspectionType';

@Component({
  selector: 'app-inspection-type-detail',
  standalone: true,
  imports: [PrimeNgModule, CommonModule,
    FormsModule],
  templateUrl: './inspection-type-detail.component.html',
  styleUrls: ['./inspection-type-detail.component.css']
})
export class InspectionTypeDetailComponent implements OnInit {
  public loading = true;
  public saving = false;

  public customers: CustomerDTO[] = [];

  public id = 0;
  public isNew = false;

  // UI validity
  public formValid = false;

  // Model used by HTML (camelCase)
  public model: InspectionTypeDetailDTO = {
    inspectionTypeId: 0,
    title: '',
    percentPacked: 0,
    isRequirements: false,
    isFactoryAudit: false,
    isProductStatus: false,
    isDefect: false,
    isProductDetails: false,
    isMeasurements: false,
    isDocuments: false,
    isIdenticalDesign: false,
    isPacking: false,
    auditTypeId: 0,
    canBeDeleted: false,
    customer: {
      customerId: 0,
      title: '',
      country: null,
      city: null,
      zip: null,
      street: null,
      contactPersons: [],
      canBeDeleted: false
    }
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private masterData: MasterDataService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id') ?? 0);
    this.isNew = this.id === 0;

    this.loadOverviewAndInit();
  }

  // ---------------------------
  // Load customers + existing
  // ---------------------------
  private loadOverviewAndInit(): void {
    this.loading = true;

    if (this.isNew) {
      this.loadNewInspectionType();
      return;
    }

    this.masterData.getInspectionTypeDetail(this.id)
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response: InspectionTypeDetailDTO) => {

          // Kundenliste für Dropdown
          this.customers = response.customerList ?? [];

          // Bestehender Datensatz
          if (!response.inspectionTypeId) {
            this.notification('Inspection Type not found', 'error');
            this.router.navigate(['/inspectiontypes']);
            return;
          }

          this.model = this.mapFromDetailDto(response);
          this.recomputeFormValid();
        },
        error: (err) => {
          console.error('❌ Error loading detail:', err);
          this.notification('Error loading detail', 'error');
        }
      });
  }

  private loadNewInspectionType(): void {
    this.masterData.getInspectionTypeOverview()
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response: InspectionTypeOverviewResponse) => {
          this.customers = response.customers ?? [];
          this.model = this.createEmptyModel();
          this.preselectCustomerFromQueryParam();
          this.recomputeFormValid();
        },
        error: (err) => {
          console.error('❌ Error loading overview:', err);
          this.notification('Error loading data', 'error');
        }
      });
  }

  private createEmptyModel(): InspectionTypeDetailDTO {
    return {
      inspectionTypeId: 0,
      title: '',
      percentPacked: 0,
      isRequirements: false,
      isFactoryAudit: false,
      isProductStatus: false,
      isDefect: false,
      isProductDetails: false,
      isMeasurements: false,
      isDocuments: false,
      isIdenticalDesign: false,
      isPacking: false,
      auditTypeId: 0,
      canBeDeleted: false,
      customer: null
    };
  }

  private preselectCustomerFromQueryParam(): void {
    const qpCustomerId = Number(this.route.snapshot.queryParamMap.get('customerId') ?? 0);
    if (!qpCustomerId) return;

    this.model.customer = this.customers.find(c => c.customerId === qpCustomerId) ?? null;
  }

  private mapFromDetailDto(dto: InspectionTypeDetailDTO): InspectionTypeDetailDTO {
    return {
      auditTypeId: dto.auditTypeId,
      inspectionTypeId: dto.inspectionTypeId!,
      title: dto.title,
      percentPacked: dto.percentPacked,
      customer: dto.customer,
      canBeDeleted: dto.canBeDeleted,

      isRequirements: dto.isRequirements,
      isFactoryAudit: dto.isFactoryAudit,
      isProductStatus: dto.isProductStatus,
      isDefect: dto.isDefect,
      isProductDetails: dto.isProductDetails,
      isMeasurements: dto.isMeasurements,
      isDocuments: dto.isDocuments,
      isIdenticalDesign: dto.isIdenticalDesign,
      isPacking: dto.isPacking
    };
  }

  // ---------------------------
  // UI helpers
  // ---------------------------
  public onCustomerChange(_: CustomerDTO | null): void {
    this.recomputeFormValid();
  }

  public recomputeFormValid(): void {
    const titleOk = !!this.model.title && this.model.title.trim().length > 0;
    const percentOk =
      this.model.percentPacked !== null &&
      this.model.percentPacked !== undefined &&
      Number(this.model.percentPacked) >= 0 &&
      Number(this.model.percentPacked) <= 100;

    const customerOk = !!this.model.customer && !!this.model.customer.customerId;

    this.formValid = titleOk && percentOk && customerOk;
  }

  public goBack(): void {
    this.router.navigate(['/inspectiontypes']);
  }

  // ---------------------------
  // Save
  // ---------------------------
  public save(): void {
    this.recomputeFormValid();
    if (!this.formValid) {
      this.notification('Please fill all required fields', 'warning');
      return;
    }

    if (this.saving) return;
    this.saving = true;

    if (this.isNew) {
      this.masterData.createInspectionType(this.model).subscribe({
        next: (resp: any) => {
          const newId = typeof resp === 'number'
            ? resp
            : resp?.inspectionTypeId ?? resp?.auditTypeId ?? resp?.id ?? 0;

          if (!newId) {
            this.notification('Saved, but no ID returned', 'warning');
            this.saving = false;
            return;
          }

          this.notification('Inspection Type created', 'success');

          this.router.navigate(['/inspectionTypeDetail', newId]);
        },
        error: (err) => {
          console.error('❌ Create Error:', err);
          this.notification('Error creating Inspection Type', 'error');
          this.saving = false;
        }
      });
    } else {
      this.masterData.updateInspectionType(this.id, this.model).subscribe({
        next: () => {
          this.notification('Inspection Type updated', 'success');
          this.saving = false;
        },
        error: (err) => {
          console.error('❌ Update Error:', err);
          this.notification('Error updating Inspection Type', 'error');
          this.saving = false;
        }
      });
    }
  }

  // ---------------------------
  // ID helper (handles auditTypeId)
  // ---------------------------
  private getId(item: InspectionTypeDetailDTO): number {
    const anyItem: any = item as any;
    return (anyItem.auditTypeId ?? item.inspectionTypeId ?? 0) as number;
  }

  // ---------------------------
  // Mapping
  // ---------------------------
  private mapFromDto(dto: InspectionTypeDetailDTO): any {
    const anyDto: any = dto as any;

    return {
      inspectionTypeId: this.getId(dto),
      title: dto.title ?? '',
      percentPacked: dto.percentPacked ?? 0,
      customer: dto.customer ?? null,

      // support both camelCase and legacy PascalCase payloads
      isRequirements: anyDto.isRequirements ?? anyDto.ISRequirements ?? false,
      isFactoryAudit: anyDto.isFactoryAudit ?? anyDto.ISFactoryAudit ?? false,

      isProductStatus: anyDto.isProductStatus ?? anyDto.ISProductStatus ?? false,
      isDefect: anyDto.isDefect ?? anyDto.ISDefect ?? false,
      isProductDetails: anyDto.isProductDetails ?? anyDto.ISProductDetails ?? false,
      isMeasurements: anyDto.isMeasurements ?? anyDto.ISMeasurements ?? false,
      isDocuments: anyDto.isDocuments ?? anyDto.ISDocuments ?? false,
      isIdenticalDesign: anyDto.isIdenticalDesign ?? anyDto.ISIdenticalDesign ?? false,

      isPacking: anyDto.isPacking ?? anyDto.ISPacking ?? false
    };
  }

  // ---------------------------
  // Notification
  // ---------------------------
  private notification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: type, icon: true }
    });
  }
}
